import { isAccountSyncStatus, type AccountSyncStatus, type AdAccountSummary } from '@/types';

/**
 * 首頁「廣告帳戶」小卡的狀態字判準(R298)。
 * -----------------------------------------------------------------------------
 * **改版前小卡只看 `status`**:`ACTIVE` → 「已連線」、其餘 → 「需重新授權」。於是
 * 一顆在 Google 端未啟用的帳戶(BE 寫 `sync_status = ACCOUNT_NOT_ENABLED`,而
 * `status` **仍然是 `ACTIVE`**)在首頁被寫成「已連線」—— 同一份 payload、同一顆帳戶,
 * 廣告串接頁畫得出「未啟用」而首頁說一切正常(owner 2026-09-02 回報)。
 *
 * ⚠️ **兩個欄位問的是不同問題,不可以合成一個布林**:
 *   - `status`(`ACTIVE` | `TOKEN_EXPIRED`)= **這把授權還有效嗎** → 壞了要重新授權
 *   - `sync_status` = **最近一次同步發生了什麼** → 壞了的解法看原因(未啟用要去
 *     Google Ads 啟用,重新授權沒有用)
 * 所以優先序是「連結層問題 > 同步狀態」:`status` 壞掉時同步一定也做不成,但畫面上
 * 該給的動作是重新授權。
 *
 * ⚠️ **`syncStatus` 拿不到時只講「已連線」,不可以補 `NEVER`** —— 那是 R263 那張
 * 回報單的病灶(把「不知道」翻譯成「從未同步過」)。「已連線」講的是 `status` 這件
 * 我們真的知道的事,不對同步狀態表態。
 *
 * ⚠️ **認不得的 `syncStatus` 顯 wire 原值**(R296 / R297 同一條):BE 的值域是開放的,
 * 猜一個已知值等於謊報;而「狀態不明」把可查、可轉述的線索歸零。
 */
export type HomeAccountTone = 'ok' | 'warn' | 'bad';

export type HomeAccountStatus =
  /** 只知道「這把授權有效」(沒有同步狀態可講)→ i18n `side.accounts.linked`。 */
  | { kind: 'linked'; tone: HomeAccountTone }
  /** 連結層失效(`status !== 'ACTIVE'`)→ i18n `side.accounts.needsReauth`。 */
  | { kind: 'reauth'; tone: HomeAccountTone }
  /**
   * R299 — Meta 帳戶在平台端已停用(`account_status === 'DISABLED'`)→ i18n
   * `side.accounts.platformDisabled`。Google 的同一件事住在 `sync_status === 'ACCOUNT_NOT_ENABLED'`
   * (走 `sync` 那格);Meta 沒有同步,唯一的線索是這一欄 —— 不看它,首頁又會對一顆
   * 平台端停用的帳戶說「已連線」(R298 同型)。
   */
  | { kind: 'platformDisabled'; tone: HomeAccountTone }
  /** 已知同步狀態 → i18n `side.accounts.sync<VALUE>`。 */
  | { kind: 'sync'; value: AccountSyncStatus; tone: HomeAccountTone }
  /** 認不得的 wire 值 → 原樣顯出來。 */
  | { kind: 'raw'; raw: string; tone: HomeAccountTone };

/**
 * 已知同步狀態 → 小卡色調。
 *
 * `Record<AccountSyncStatus, …>` 是**編譯期閘**:有人往 `ACCOUNT_SYNC_STATUSES`
 * 加值卻沒決定色調,tsc 就紅(同 `AdsAccountsPage.SYNC_STATUS_PALETTE` 的用法)。
 * `RUNNING` 算 `ok` —— 進行中不是待辦。
 */
const TONE_BY_SYNC_STATUS: Record<AccountSyncStatus, HomeAccountTone> = {
  OK: 'ok',
  RUNNING: 'ok',
  NEVER: 'warn',
  FAILED: 'bad',
  TOKEN_EXPIRED: 'bad',
  ACCOUNT_NOT_ENABLED: 'bad',
};

export function homeAccountStatus(
  a: Pick<AdAccountSummary, 'status' | 'syncStatus' | 'platform' | 'accountStatus'>,
): HomeAccountStatus {
  // ① 連結層優先:`status` 是 open str(R299),`!== 'ACTIVE'` = 「授權已失效」(Google
  //    `TOKEN_EXPIRED` / Meta `REVOKED` / 未知值都歸這裡 —— fail-safe 方向是提示重授權)
  if (a.status !== 'ACTIVE') return { kind: 'reauth', tone: 'bad' };
  // ①.5 R299 — Meta 平台端停用(重新授權沒有用,要去 Meta 後台啟用)
  if (a.platform === 'meta' && a.accountStatus === 'DISABLED') {
    return { kind: 'platformDisabled', tone: 'bad' };
  }
  const s = a.syncStatus;
  // ② 沒有同步狀態(舊 BE 缺欄 / 空字串)→ 只講授權有效這件事
  if (typeof s !== 'string' || s === '') return { kind: 'linked', tone: 'ok' };
  // ③ 認得的值 → 它自己的文案與色調
  if (isAccountSyncStatus(s)) return { kind: 'sync', value: s, tone: TONE_BY_SYNC_STATUS[s] };
  // ④ 認不得 → 顯原值 + 中性色(不猜、也不說「狀態不明」)
  return { kind: 'raw', raw: s, tone: 'warn' };
}

/**
 * 這顆帳戶是不是「在 Google 端未啟用」——「需要你處理」那一區的計數判準。
 *
 * ⚠️ **與「需重新授權」刻意分開計數**(BE commit `41cd785` 明寫同一條理由):
 * 一個要去 Google Ads 啟用、一個要重新授權,混成一個數字使用者會去按沒用的那個。
 * ⚠️ 未啟用帳戶的 `status` **仍是 `ACTIVE`** → 不會被 `status !== 'ACTIVE'` 那條
 * 重複計到,兩張卡不會雙數。
 */
export function accountNotEnabled(
  a: Pick<AdAccountSummary, 'status' | 'syncStatus' | 'platform' | 'accountStatus'>,
): boolean {
  const st = homeAccountStatus(a);
  return st.kind === 'sync' && st.value === 'ACCOUNT_NOT_ENABLED';
}
